'use client';

import { GraphicsProvider } from '@/features/lobby/settings/GraphicsContext';
import { LobbyNav } from '@/features/lobby/LobbyNav';

/**
 * Thin client wrapper that mounts the lobby's `LobbyNav` chrome on
 * the marketing landing.
 *
 * `LobbyNav` consumes a few lobby-scoped providers transitively:
 *
 *   • `useGraphics` (via `PortfolioDropdown`) — REQUIRED. The hook
 *     throws when no `GraphicsProvider` is mounted above it, so we
 *     wrap the nav with one here. The provider's lazy initialisers
 *     run a one-shot GPU probe + localStorage read, which is the same
 *     work the `/lobby` route does anyway — keeping the provider tree
 *     identical means the portfolio menu's quality picker reads the
 *     viewer's actual saved preference on both surfaces.
 *
 *   • `useLaunchTransition` (via the Create nav item) — SAFE WITHOUT
 *     A PROVIDER. The hook returns `{ begin: () => {}, active: false }`
 *     when no `LaunchTransitionProvider` is mounted. The Create
 *     button's onClick still navigates via `router.push('/create')` so
 *     the destination remains reachable; only the entrance choreography
 *     is skipped.
 *
 *   • `useLeaderboardModal`, `useLobbyHeaderStats`, `useWallet` — all
 *     ride on globally-mounted providers in `src/providers/index.tsx`.
 *
 * `LobbyNav` requires `skybox` + `onSkyboxChange` props (used by the
 * lobby for sky-aware HUD trims — currently unused by the nav body)
 * and they aren't applicable on the landing, so we pin them to `null`
 * / no-op.
 *
 * The wrapper exists because `LandingPage` is a server component and
 * can't pass the `onSkyboxChange` callback prop directly to a client
 * component — the function would need to cross the server/client
 * boundary, which Next.js disallows.
 */
export function LandingNav() {
  return (
    <GraphicsProvider>
      <LobbyNav skybox={null} onSkyboxChange={() => {}} />
    </GraphicsProvider>
  );
}
